import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Fab,
  Paper,
  IconButton,
  TextField,
  Button,
  Stack,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import axios from "axios";

// Yantra brand mark, served from /public/yantra_icon.svg
// `inverted` = true renders white via CSS filter (for use on the orange header)
const YantraLogo = ({ size = 24, inverted = false }) => (
  <img
    src="/yantra_icon.svg"
    alt="Yantra"
    width={size}
    height={size}
    style={{
      display: "block",
      filter: inverted ? "brightness(0) invert(1)" : "none",
    }}
  />
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const QUICK_MACHINE = [
  { label: "Shift status?", q: "What is the current state of this shift and what should I check first?" },
  { label: "Why low utilization?", q: "Why is utilization low this shift, based on the snapshot?" },
  { label: "Anything unusual?", q: "Looking at the snapshot, is anything unusual or worth investigating?" },
  { label: "Component check", q: "Walk me through each allocated component — are any underperforming or overdue?" },
  { label: "Rejections", q: "How are rejections trending this shift? Anything to flag?" },
];

const QUICK_FLOOR = [
  { label: "Where do I look first?", q: "Which machine should I look at first right now? Why?" },
  { label: "Rank by OEE", q: "Rank all machines by OEE this shift, lowest to highest, with the headline reason for each." },
  { label: "Anyone disconnected?", q: "Are any machines disconnected, idle for too long, or showing stale telemetry?" },
  { label: "Floor health", q: "Give me a one-line health summary of the floor right now." },
  { label: "Production gaps", q: "Which machines are furthest behind their parts target this shift?" },
];

/**
 * Smart Supervisor Buddy — floating chat widget.
 * Two modes:
 *  - This Machine: scoped to selectedMachineName, hits /api/buddy/ask
 *  - All Machines: floor-wide, hits /api/buddy/ask-floor
 */
export default function SmartBuddy({ selectedShift, selectedMachineName }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('overview'); // 'overview' | 'ask'
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewTab, setOverviewTab] = useState('grid'); // grid|priority|score|floor|trend|top|components|cost|bottleneck
  const [maximized, setMaximized] = useState(false);
  const [mode, setMode] = useState("machine"); // 'machine' | 'floor'
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const shiftNo = String(selectedShift || "").replace(/^Shift\s*/i, "").trim();
  const machineReady = !!shiftNo && !!selectedMachineName;
  const floorReady = !!shiftNo;
  const ready = mode === "machine" ? machineReady : floorReady;

  // Reset history when switching modes (avoids confusing context mixing)
  const handleModeChange = (_e, newMode) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    setHistory([]);
    setInput("");
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const ask = async (question) => {
    if (!question.trim() || !ready || loading) return;
    setHistory((h) => [...h, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    try {
      const base = window._env_.SERVER_URL2.replace(/\/$/, "");
      // Scope buddy to the currently logged-in supervisor's customer
      const rawCustomerId = localStorage.getItem("CustomerID") || "";
      const customer_id = rawCustomerId.replace(/\\/g, "").replace(/"/g, "").trim();
      let url, body;
      if (mode === "floor") {
        url = `${base}/api/buddy/ask-floor`;
        body = { question, context: { shift_no: shiftNo, customer_id } };
      } else {
        url = `${base}/api/buddy/ask`;
        body = {
          question,
          context: { shift_no: shiftNo, machine_name: selectedMachineName, customer_id },
        };
      }
      const { data } = await axios.post(url, body, { timeout: 120000 });
      setHistory((h) => [
        ...h,
        { role: "assistant", text: data?.answer || "(empty response)" },
      ]);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "request failed";
      setHistory((h) => [...h, { role: "error", text: `Buddy error: ${msg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = mode === "floor" ? QUICK_FLOOR : QUICK_MACHINE;
  const subtitle =
    mode === "floor"
      ? `All machines — Shift ${shiftNo || "?"}`
      : machineReady
      ? `${selectedMachineName} — Shift ${shiftNo}`
      : "Select a machine + shift";

  // Reset overview when the panel closes so the loading animation shows again
  // on next open (instead of flashing stale data while the new fetch is in flight).
  useEffect(() => {
    if (!open) {
      setOverview(null);
      setOverviewLoading(false);
    }
  }, [open]);

  // Fetch structured overview when Overview view is opened or shift changes
  useEffect(() => {
    if (!open || view !== 'overview' || !floorReady) return;
    let cancelled = false;
    const fetchOv = async () => {
      setOverviewLoading(true);
      try {
        const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
        const customerId = localStorage.getItem('CustomerID') || '';
        const r = await axios.post(`${base}/api/buddy/structured-overview`, {
          context: { shift_no: shiftNo, customer_id: customerId.replace(/[\s"']/g, '') },
        });
        if (!cancelled) setOverview(r.data);
      } catch (e) {
        if (!cancelled) setOverview({ error: e?.response?.data?.message || e?.message || 'Failed to load' });
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    };
    fetchOv();
    const id = setInterval(fetchOv, 30000); // refresh every 30s while open
    return () => { cancelled = true; clearInterval(id); };
  }, [open, view, shiftNo, floorReady]);

  // Helper components for the visual cards
  const TrafficDot = ({ color }) => {
    const c = color === 'red' ? '#dc2626' : color === 'amber' ? '#f59e0b' : color === 'green' ? '#16a34a' : '#9ca3af';
    return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c, marginRight: 6 }} />;
  };
  const Donut = ({ pct, color = '#16a34a', size = 80 }) => {
    const r = 30; const c = 2 * Math.PI * r;
    const off = c - (Math.min(100, Math.max(0, pct)) / 100) * c;
    return (
      <svg width={size} height={size} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
                strokeDasharray={c} strokeDashoffset={off}
                transform="rotate(-90 40 40)" strokeLinecap="round" />
        <text x="40" y="46" textAnchor="middle" fontSize="18" fontWeight="800" fill="#111827">{Math.round(pct)}%</text>
      </svg>
    );
  };
  // Animated number counter — counts from 0 to target value on mount/change
  const CountUp = ({ value, suffix = '', duration = 600 }) => {
    const target = Number(value) || 0;
    const prevRef = React.useRef(target);
    const [n, setN] = React.useState(target);
    React.useEffect(() => {
      const from = prevRef.current;
      if (from === target) return; // no change → no animation
      let raf = 0;
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setN(from + (target - from) * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
        else { setN(target); prevRef.current = target; }
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return <>{Math.round(n).toLocaleString('en-IN')}{suffix}</>;
  };

  // Parse "icon label" into separate emoji + label
  const splitIconLabel = (title) => {
    const m = String(title || '').match(/^([\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u26A0\uFE0F]+)\s*(.*)$/u);
    return m ? { icon: m[1], label: m[2] } : { icon: '', label: title || '' };
  };
  const MiniCard = ({ onClick, bg, title, big, sub, idx, pulse }) => {
    const { icon, label } = splitIconLabel(title);
    return (
      <button onClick={onClick} className={pulse ? "ys-pulse-red" : ""} style={{
        background: bg || '#fff', border: 'none', borderRadius: 12,
        padding: maximized ? 18 : 14, textAlign: 'left', cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minHeight: maximized ? 130 : 110,
        display: 'flex', flexDirection: 'column', gap: 6,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: maximized ? 22 : 18, lineHeight: 1 }}>{icon}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 0.2 }}>{label}</span>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: maximized ? 26 : 20, fontWeight: 800, color: '#111827', lineHeight: 1.15, wordBreak: 'break-word' }}>{big}</div>
          {sub && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{sub}</div>
          )}
        </div>
      </button>
    );
  };

  // DrilldownChat seeds the Ask Anything input with a context-specific prompt.
  // Every drilldown prompt comes from the Overview tab and references the whole
  // floor — "Which machine has X", "How does today compare to last week", etc.
  // Always switch to floor mode so the Yantra Intelligence has the floor-wide snapshot; machine
  // mode would scope the answer to only the currently-selected machine.
  const DrilldownChat = ({ context }) => (
    <button onClick={() => {
      if (mode !== 'floor') {
        setMode('floor');
        setHistory([]);
      }
      setView('ask');
      setInput(context);
    }}
      style={{
        marginTop: 12, padding: '8px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6,
        cursor: 'pointer', border: '1px solid #F3951D', background: '#fff', color: '#F3951D',
        display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center',
      }}>
      💬 Ask Buddy about this
    </button>
  );

  const Sparkline = ({ data, color = '#3b82f6', wide = false }) => {
    if (!data || data.length === 0) return null;
    const w = wide ? 460 : 280, h = wide ? 110 : 70, pad = 6;
    const vals = data.map((d) => Number(d.value) || 0);
    const max = Math.max(...vals, 1);
    const pts = vals.map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / Math.max(1, vals.length - 1);
      const y = h - pad - ((v / max) * (h - pad * 2));
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polyline className="ys-spark" points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        {vals.map((v, i) => {
          const x = pad + (i * (w - pad * 2)) / Math.max(1, vals.length - 1);
          const y = h - pad - ((v / max) * (h - pad * 2));
          return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
        })}
      </svg>
    );
  };

  return (
    <>
      <style>{`
        @keyframes ys-buddy-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ys-buddy-expand-in {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes ys-modal-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes ys-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .ys-modal { animation: ys-modal-in 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .ys-backdrop { animation: ys-backdrop-in 180ms ease-out both; }
        @keyframes ys-buddy-collapse-out {
          from { opacity: 1; transform: scale(1); }
          to   { opacity: 0; transform: scale(0.92); }
        }
        @keyframes ys-buddy-pulse-red {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
          50%      { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
        }
        @keyframes ys-buddy-shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        @keyframes ys-buddy-draw {
          from { stroke-dashoffset: 600; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes ys-buddy-fill {
          from { width: 0%; }
        }
        .ys-mc { animation: ys-buddy-fade-in 360ms cubic-bezier(0.34,1.56,0.64,1) both; }
        .ys-detail { animation: ys-buddy-expand-in 280ms cubic-bezier(0.16, 1, 0.3, 1) both; transform-origin: center top; }
        .ys-pulse-red { animation: ys-buddy-pulse-red 1.8s ease-in-out infinite; }
        .ys-shimmer {
          background: linear-gradient(90deg, #e5e7eb 25%, #f9fafb 50%, #e5e7eb 75%);
          background-size: 400px 100%;
          animation: ys-buddy-shimmer 1.4s linear infinite;
        }
        .ys-spark { stroke-dasharray: 600; animation: ys-buddy-draw 1.2s ease-out forwards; }
        .ys-bar-fill { animation: ys-buddy-fill 700ms cubic-bezier(0.16,1,0.3,1) both; }
        .ys-grid-wrap { animation: ys-buddy-fade-in 220ms ease-out both; }
      `}</style>
      {!open && (
        <Tooltip title="Ask Smart Buddy" arrow>
          <span style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}>
            <Fab
              onClick={() => setOpen(true)}
              sx={{
                bgcolor: "#ffffff",
                border: "2px solid #F3951D",
                "&:hover": { bgcolor: "#fff8ec" },
              }}
            >
              <YantraLogo size={32} />
            </Fab>
          </span>
        </Tooltip>
      )}

      {open && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: maximized ? 0 : 24,
            right: maximized ? 0 : 24,
            top: maximized ? 0 : 'auto',
            left: maximized ? 0 : 'auto',
            width: maximized ? '100vw' : 520,
            height: maximized ? '100vh' : 720,
            maxWidth: '100vw',
            maxHeight: '100vh',
            display: "flex",
            flexDirection: "column",
            zIndex: 1300,
            borderRadius: maximized ? 0 : 2,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ bgcolor: "#F3951D", color: "white", px: 2, py: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <YantraLogo size={26} inverted />
              <Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                  Smart Buddy
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.1 }}>
                  {subtitle}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => setMaximized(m => !m)} sx={{ color: "white" }} title={maximized ? "Restore" : "Maximize"}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  {maximized ? (
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                  ) : (
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                  )}
                </svg>
              </IconButton>
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "white" }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          {/* Top view toggle: Overview vs Ask */}
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ p: 1, borderBottom: "1px solid #eee", bgcolor: "#fffbeb" }}
          >
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => v && setView(v)}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none", px: 2, fontWeight: 700,
                  "&.Mui-selected": { bgcolor: "#F3951D", color: "white", "&:hover": { bgcolor: "#d97706" } },
                },
              }}
            >
              <ToggleButton value="overview">📊 Overview</ToggleButton>
              <ToggleButton value="ask">💬 Ask Anything</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Existing Mode toggle (This Machine / All Machines) only when in Ask view */}
          {view === 'ask' && (
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ p: 1, borderBottom: "1px solid #eee", bgcolor: "#fff" }}
          >
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 2,
                  "&.Mui-selected": {
                    bgcolor: "#F3951D",
                    color: "white",
                    "&:hover": { bgcolor: "#d97706" },
                  },
                },
              }}
            >
              <ToggleButton value="machine" disabled={!machineReady}>
                This Machine
              </ToggleButton>
              <ToggleButton value="floor">All Machines</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          )}

          {/* OVERVIEW VIEW — visual cards */}
          {view === 'overview' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f9fafb' }}>
              {/* Back button when a card is opened */}
              {overviewTab !== 'grid' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <button onClick={() => setOverviewTab('grid')}
                    style={{
                      padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                      border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      transition: 'transform 0.15s ease, background 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'none'; }}>
                    ← Back
                  </button>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#9ca3af' }}>
                    {overviewLoading ? 'updating…' : 'auto-refresh 30s'}
                  </span>
                </div>
              )}

              {/* MINI-CARD GRID (default Overview landing) */}
              {overviewTab === 'grid' && overview && !overview.error && (
                <div key="grid">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Tap any tile for detail</span>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{overviewLoading ? 'updating…' : 'auto-refresh 30s'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: maximized ? 'repeat(4,1fr)' : 'repeat(2,1fr)', gap: 10 }}>
                    {/* Priority */}
                    <MiniCard onClick={() => setOverviewTab('priority')} idx={0} pulse={overview.priority_now?.traffic_light === 'red'}
                      bg={overview.priority_now?.traffic_light === 'red' ? '#fee2e2' : overview.priority_now?.traffic_light === 'amber' ? '#fef3c7' : '#ecfccb'}
                      title="⚠️ Priority"
                      big={overview.priority_now?.machine_name || 'All OK'}
                      sub={overview.priority_now?.headline || ''} />
                    {/* Score */}
                    <MiniCard onClick={() => setOverviewTab('score')} idx={1}
                      bg="#eff6ff"
                      title="📊 Score"
                      big={`${overview.today_score?.pct_target ?? 0}%`}
                      sub={`${overview.today_score?.parts_made ?? 0} / ${overview.today_score?.parts_target ?? 0} parts`} />
                    {/* Trend */}
                    <MiniCard onClick={() => setOverviewTab('trend')} idx={2}
                      bg="#dbeafe"
                      title="📈 Trend"
                      big="7d"
                      sub="" />
                    {/* Top performers */}
                    <MiniCard onClick={() => setOverviewTab('top')} idx={3}
                      bg="#dcfce7"
                      title="🏆 Top Performers"
                      big={overview.top_performers?.[0]?.name?.split('-').slice(-2).join('-') || '—'}
                      sub={overview.top_performers?.[0] ? `${overview.top_performers[0].parts_made} parts (${overview.top_performers[0].pct}%)` : 'no data yet'} />
                    {/* Components Now */}
                    <MiniCard onClick={() => setOverviewTab('components')} idx={4}
                      bg="#fef9c3"
                      title="📦 Components"
                      big={`${overview.components_now?.length ?? 0}`}
                      sub="parts now" />
                    {/* Cost */}
                    <MiniCard onClick={() => setOverviewTab('cost')} idx={5}
                      bg="#ccfbf1"
                      title="💰 Cost"
                      big={overview.cost_today?.revenue != null ? `₹${overview.cost_today.revenue.toLocaleString('en-IN')}` : '—'}
                      sub={overview.cost_today?.note ? 'add rates+costs' : `${overview.cost_today?.machines_priced ?? 0}/${overview.cost_today?.machines_total ?? 0} priced`} />
                    {/* Bottleneck */}
                    <MiniCard onClick={() => setOverviewTab('bottleneck')} idx={6}
                      bg="#fee2e2"
                      title="🚧 Bottleneck"
                      big={overview.bottleneck?.[0]?.name?.split('-').slice(-2).join('-') || '—'}
                      sub={overview.bottleneck?.[0] ? `gap ${overview.bottleneck[0].gap} parts` : 'all on target'} />
                    {/* Quality */}
                    <MiniCard onClick={() => setOverviewTab('quality')} idx={7}
                      bg="#fce7f3"
                      title="♻️ Quality"
                      big={`${overview.quality?.total_rejections ?? 0}`}
                      sub={overview.quality?.machines_with_rejects ? `${overview.quality.machines_with_rejects} machines` : 'rejections today'} />
                    {/* Downtime */}
                    <MiniCard onClick={() => setOverviewTab('downtime')} idx={8}
                      bg="#e0e7ff"
                      title="🛑 Downtime"
                      big={`${overview.downtime?.total_minutes ?? 0} min`}
                      sub={overview.downtime?.top_reasons?.[0] ? overview.downtime.top_reasons[0].name : 'no downtime logged'} />
                    {/* Shift Pace */}
                    <MiniCard onClick={() => setOverviewTab('pace')} idx={9}
                      bg={overview.shift_pace?.status === 'behind' ? '#fee2e2' : overview.shift_pace?.status === 'ahead' ? '#dcfce7' : '#fef3c7'}
                      title="⏰ Shift Pace"
                      big={overview.shift_pace ? `${overview.shift_pace.parts_pct}% / ${overview.shift_pace.elapsed_pct}%` : '—'}
                      sub={overview.shift_pace ? `${overview.shift_pace.status} · ${overview.shift_pace.minutes_left}m left` : 'no shift data'} />
                    {/* Operators */}
                    <MiniCard onClick={() => setOverviewTab('operators')} idx={10}
                      bg="#cffafe"
                      title="👨‍🔧 Operators"
                      big={overview.operator_productivity?.rows?.length ? `${overview.operator_productivity.rows[0].name}` : '—'}
                      sub={overview.operator_productivity?.rows?.length ? `top: ${overview.operator_productivity.rows[0].parts} parts` : 'no operator data'} />
                  </div>
                </div>
              )}

              {/* CENTERED MODAL OVERLAY rendered via Portal so it escapes Paper's containing block */}
              {overviewTab !== 'grid' && createPortal(
                <>
                  <div
                    className="ys-backdrop"
                    onClick={() => setOverviewTab('grid')}
                    style={{
                      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                      zIndex: 2000, cursor: 'pointer',
                    }}
                  />
                  <div
                    className="ys-modal"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 'min(640px, 92vw)', maxHeight: '88vh', overflowY: 'auto',
                      background: '#f9fafb', borderRadius: 14, padding: 18,
                      boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                      zIndex: 2001,
                    }}
                  >

              {/* Animated loading state — shows immediately on first render (before
                  useEffect fires). Uses inline SVG so it doesn't depend on any external
                  asset. v2 marker below confirms which build is live. */}
              {!overview && overviewTab === 'grid' && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '50px 20px', minHeight: 280, gap: 18,
                  background: '#fffaf0',
                  border: '2px solid #f97316',
                  borderRadius: 8,
                }}>
                  {/* Static fallback so something is ALWAYS visible even if animations fail */}
                  <div style={{ fontSize: 48 }}>🤖</div>
                  <div style={{
                    position: 'relative', width: 88, height: 88,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(249,115,22,0.3) 0%, rgba(249,115,22,0) 70%)',
                      animation: 'sb-pulse 1.6s ease-in-out infinite',
                    }} />
                    <div style={{
                      position: 'absolute', inset: 8, borderRadius: '50%',
                      border: '3px dashed #f97316', borderTopColor: 'transparent',
                      animation: 'sb-spin 2.4s linear infinite',
                    }} />
                    {/* Inline SVG Yantra-style mark — no external asset dependency */}
                    <svg width="44" height="44" viewBox="0 0 100 100" style={{ animation: 'sb-bob 2s ease-in-out infinite', position: 'relative' }}>
                      <circle cx="50" cy="50" r="36" fill="none" stroke="#f97316" strokeWidth="6" />
                      <circle cx="50" cy="50" r="10" fill="#f97316" />
                    </svg>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>
                      Smart Buddy is reading the floor…
                    </div>
                    <div style={{ fontSize: 11, color: '#9a3412' }}>
                      Stitching shift snapshot, KPIs and bottlenecks
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#f97316',
                        animation: `sb-dot 1.2s ease-in-out ${i * 0.18}s infinite`,
                      }} />
                    ))}
                  </div>
                  <style>{`
                    @keyframes sb-pulse { 0%,100% { transform: scale(0.9); opacity: 0.6; } 50% { transform: scale(1.1); opacity: 1; } }
                    @keyframes sb-spin  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    @keyframes sb-bob   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
                    @keyframes sb-dot   { 0%,80%,100% { opacity: 0.3; transform: scale(0.85); } 40% { opacity: 1; transform: scale(1.1); } }
                  `}</style>
                </div>
              )}
              {overview?.error && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6 }}>{overview.error}</div>
              )}

              {/* PRIORITY NOW */}
              {overview && !overview.error && overviewTab === 'priority' && overview.priority_now && (
                <div style={{
                  background: '#fff', border: '2px solid ' + (overview.priority_now.traffic_light === 'red' ? '#dc2626' : overview.priority_now.traffic_light === 'amber' ? '#f59e0b' : '#16a34a'),
                  borderRadius: 10, overflow: 'hidden',
                }}>
                  {overview.priority_now.image_url ? (
                    <div style={{ height: maximized ? 280 : 200, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={`${(window._env_?.SERVER_URL2 || '').replace(/\/$/, '')}${overview.priority_now.image_url}`}
                           alt={overview.priority_now.machine_name}
                           style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                    </div>
                  ) : (
                    <div style={{ height: maximized ? 200 : 120, background: overview.priority_now.traffic_light === 'red' ? '#fee2e2' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: maximized ? 72 : 48 }}>
                      {overview.priority_now.traffic_light === 'red' ? '⚠️' : overview.priority_now.traffic_light === 'amber' ? '🟡' : '✅'}
                    </div>
                  )}
                  <div style={{ padding: maximized ? 24 : 18 }}>
                    {overview.priority_now.machine_name && (
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Look at this first</div>
                    )}
                    <div style={{ fontSize: maximized ? 32 : 22, fontWeight: 800, color: '#111827', marginTop: 2 }}>
                      {overview.priority_now.machine_name || 'All machines healthy'}
                    </div>
                    <div style={{ fontSize: maximized ? 20 : 16, color: '#374151', margin: '8px 0 14px' }}>
                      <TrafficDot color={overview.priority_now.traffic_light} />
                      {overview.priority_now.headline}
                    </div>
                    {overview.priority_now.parts_target > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                          <div className="ys-bar-fill" style={{ width: `${Math.min(100, (overview.priority_now.parts_made / Math.max(1, overview.priority_now.parts_target)) * 100)}%`, height: '100%', background: overview.priority_now.traffic_light === 'red' ? '#dc2626' : '#16a34a' }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
                          <CountUp value={overview.priority_now.parts_made} /> / {overview.priority_now.parts_target}
                        </span>
                      </div>
                    )}
                    <DrilldownChat context={`Tell me more about ${overview.priority_now.machine_name || 'the priority machine'} — what's the next action?`} />
                    {overview.priority_now.action_label && (
                      <Button variant="contained" size="small" fullWidth
                        sx={{ bgcolor: overview.priority_now.traffic_light === 'red' ? '#dc2626' : '#F3951D', '&:hover': { bgcolor: overview.priority_now.traffic_light === 'red' ? '#991b1b' : '#ef6c00' } }}>
                        {overview.priority_now.action_label}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* TODAY'S SCORE */}
              {overview && !overview.error && overviewTab === 'score' && overview.today_score && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                    <Donut pct={overview.today_score.pct_target} color={overview.today_score.pct_target >= 80 ? '#16a34a' : overview.today_score.pct_target >= 50 ? '#f59e0b' : '#dc2626'} size={maximized ? 140 : 110} />
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Today's parts</div>
                      <div style={{ fontSize: maximized ? 36 : 28, fontWeight: 800 }}><CountUp value={overview.today_score.parts_made} /><span style={{ color: '#9ca3af', fontSize: maximized ? 22 : 18, fontWeight: 500 }}> / {overview.today_score.parts_target}</span></div>
                      <div style={{ fontSize: 12, color: '#374151' }}>Avg OEE: <strong>{overview.today_score.avg_oee}%</strong></div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    <div style={{ padding: 10, background: '#ecfccb', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: '#3f6212' }}>✅ Running</div>
                      <div style={{ fontSize: maximized ? 32 : 24, fontWeight: 800, color: '#15803d' }}>{overview.today_score.machines_running}</div>
                    </div>
                    <div style={{ padding: 10, background: '#fef3c7', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: '#92400e' }}>⏸️ Idle</div>
                      <div style={{ fontSize: maximized ? 32 : 24, fontWeight: 800, color: '#b45309' }}>{overview.today_score.machines_idle}</div>
                    </div>
                    <div style={{ padding: 10, background: '#fee2e2', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: '#991b1b' }}>⚠️ Alarm</div>
                      <div style={{ fontSize: maximized ? 32 : 24, fontWeight: 800, color: '#dc2626' }}>{overview.today_score.machines_alarm}</div>
                    </div>
                    <div style={{ padding: 10, background: '#f3f4f6', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: '#374151' }}>📵 Offline</div>
                      <div style={{ fontSize: maximized ? 32 : 24, fontWeight: 800, color: '#4b5563' }}>{overview.today_score.machines_disconnected}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* FLOOR STATUS */}
              {overview && !overview.error && overviewTab === 'floor' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Floor Status</div>
                  {(overview.operator_status || []).map((row, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < overview.operator_status.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <TrafficDot color={row.color} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{row.label}</div>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: row.color === 'red' ? '#dc2626' : row.color === 'amber' ? '#b45309' : row.color === 'green' ? '#15803d' : '#4b5563' }}>{row.count}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* TREND */}
              {overview && !overview.error && overviewTab === 'trend' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Last 7 days — Parts made</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Daily total across all machines</div>
                  <Sparkline data={(overview.trend?.days || []).slice().reverse().map((d) => ({ value: d.parts_made }))} color="#3b82f6" wide={maximized} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', fontSize: 9, color: '#6b7280', marginTop: 4 }}>
                    {(overview.trend?.days || []).slice().reverse().map((d, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>{d.date?.slice(5) || ''}</div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Last 7 days — OEE</div>
                  <Sparkline data={(overview.trend?.days || []).slice().reverse().map((d) => ({ value: d.oee || 0 }))} color="#16a34a" wide={maximized} />
                  <DrilldownChat context="How does today's production compare to the last 7 days?" />
                </div>
              )}

              {/* TOP PERFORMERS */}
              {overview && !overview.error && overviewTab === 'top' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>🏆 Top Performers Today</div>
                  {(overview.top_performers || []).length === 0 ? (
                    <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>No production yet today</div>
                  ) : (
                    (overview.top_performers || []).map((tp, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: i === 0 ? '#fde047' : i === 1 ? '#e5e7eb' : '#fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{i + 1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{tp.name}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{tp.parts_made} parts · {tp.pct}% of target · OEE {tp.oee}%</div>
                        </div>
                      </div>
                    ))
                  )}
                  <DrilldownChat context="Why are the top performers doing well? Any pattern across operator/component/shift?" />
                </div>
              )}

              {/* COMPONENTS NOW */}
              {overview && !overview.error && overviewTab === 'components' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>📦 Components Running Now</div>
                  {(overview.components_now || []).length === 0 ? (
                    <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>Nothing running on any machine</div>
                  ) : (
                    (overview.components_now || []).map((c, i) => (
                      <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{c.machine}</div>
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{c.component} {c.program_no ? <span style={{ color: '#9ca3af' }}>· prog {c.program_no}</span> : ''}</div>
                        {c.target > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(100, (c.made / Math.max(1, c.target)) * 100)}%`, height: '100%', background: '#16a34a' }} />
                            </div>
                            <span style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>{c.made}/{c.target}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <DrilldownChat context="Which components are most behind target? What's blocking them?" />
                </div>
              )}

              {/* COST + REVENUE */}
              {overview && !overview.error && overviewTab === 'cost' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>💰 Today's Cost & Output</div>
                  {overview.cost_today?.note && (
                    <div style={{ background: '#fef3c7', color: '#92400e', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                      {overview.cost_today.note}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    <div style={{ padding: 12, background: '#fee2e2', borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: '#991b1b', textTransform: 'uppercase' }}>Machine cost</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>₹ <CountUp value={overview.cost_today?.machine_cost || 0} /></div>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{overview.cost_today?.machines_priced || 0}/{overview.cost_today?.machines_total || 0} priced</div>
                    </div>
                    <div style={{ padding: 12, background: '#dcfce7', borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: '#15803d', textTransform: 'uppercase' }}>Output value</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d' }}>₹ <CountUp value={overview.cost_today?.revenue || 0} /></div>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{overview.cost_today?.components_priced || 0}/{overview.cost_today?.components_total || 0} priced</div>
                    </div>
                    <div style={{ padding: 12, background: (overview.cost_today?.margin || 0) >= 0 ? '#dbeafe' : '#fee2e2', borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: '#1e40af', textTransform: 'uppercase' }}>Margin</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: (overview.cost_today?.margin || 0) >= 0 ? '#1e40af' : '#dc2626' }}>₹ <CountUp value={overview.cost_today?.margin || 0} /></div>
                    </div>
                  </div>
                  <DrilldownChat context="Which machine has the worst cost-to-output ratio today?" />
                </div>
              )}

              {/* BOTTLENECK */}
              {overview && !overview.error && overviewTab === 'bottleneck' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>🚧 Bottleneck Machines</div>
                  {(overview.bottleneck || []).length === 0 ? (
                    <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>All machines on target</div>
                  ) : (
                    (overview.bottleneck || []).map((b, i) => (
                      <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{b.name}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: b.pct < 50 ? '#dc2626' : '#b45309' }}>{b.pct}%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, b.pct)}%`, height: '100%', background: b.pct < 50 ? '#dc2626' : '#f59e0b' }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>{b.parts_made}/{b.parts_target}</span>
                        </div>
                      </div>
                    ))
                  )}
                  <DrilldownChat context="Why is the top bottleneck machine behind? What action should I take?" />
                </div>
              )}

              {/* QUALITY */}
              {overview && !overview.error && overviewTab === 'quality' && overview.quality && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>♻️ Quality Today</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
                    <div style={{ fontSize: maximized ? 48 : 36, fontWeight: 800, color: '#dc2626' }}><CountUp value={overview.quality.total_rejections || 0} /></div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>rejections across {overview.quality.machines_with_rejects || 0} machines</div>
                  </div>
                  {(overview.quality.top_machines || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Top offenders</div>
                      {overview.quality.top_machines.map((m, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{m.machine}</span>
                          <span style={{ fontWeight: 800, color: '#dc2626' }}>{m.rejected}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <DrilldownChat context="Which components or machines are causing the most rejections? Any pattern by shift?" />
                </div>
              )}

              {/* DOWNTIME */}
              {overview && !overview.error && overviewTab === 'downtime' && overview.downtime && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>🛑 Downtime Today</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                    <div style={{ padding: 10, background: '#fee2e2', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#991b1b' }}>Unplanned</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#dc2626' }}>{overview.downtime.unplanned_minutes || 0}m</div>
                    </div>
                    <div style={{ padding: 10, background: '#dbeafe', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#1e40af' }}>Planned</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af' }}>{overview.downtime.planned_minutes || 0}m</div>
                    </div>
                    <div style={{ padding: 10, background: '#f3f4f6', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#374151' }}>Total</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{overview.downtime.total_minutes || 0}m</div>
                    </div>
                  </div>
                  {(overview.downtime.top_reasons || []).length > 0 ? (
                    <>
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Top reasons</div>
                      {overview.downtime.top_reasons.map((r, i) => {
                        const maxMin = overview.downtime.top_reasons[0].minutes || 1;
                        return (
                          <div key={i} style={{ padding: '4px 0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                              <span style={{ color: '#374151' }}>{r.name}</span>
                              <span style={{ fontWeight: 700, color: '#111827' }}>{r.minutes}m</span>
                            </div>
                            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${(r.minutes / maxMin) * 100}%`, height: '100%', background: '#f59e0b' }} />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>No downtime reasons logged today</div>
                  )}
                  <DrilldownChat context="What is the biggest downtime cause today and how to reduce it?" />
                </div>
              )}

              {/* SHIFT PACE */}
              {overview && !overview.error && overviewTab === 'pace' && overview.shift_pace && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>⏰ Shift {overview.shift_pace.shift_no} Pace</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 14 }}>
                    Status: <strong style={{ color: overview.shift_pace.status === 'behind' ? '#dc2626' : overview.shift_pace.status === 'ahead' ? '#15803d' : '#b45309' }}>{overview.shift_pace.status?.toUpperCase()}</strong>
                    {' · '}{overview.shift_pace.minutes_left}m left
                  </div>
                  {/* Two stacked bars */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#374151' }}>Time elapsed</span>
                      <span style={{ fontWeight: 700, color: '#111827' }}>{overview.shift_pace.elapsed_pct}%</span>
                    </div>
                    <div style={{ height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${overview.shift_pace.elapsed_pct}%`, height: '100%', background: '#6b7280' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#374151' }}>Parts done</span>
                      <span style={{ fontWeight: 700, color: '#111827' }}>{overview.shift_pace.parts_pct}% ({overview.shift_pace.parts_made}/{overview.shift_pace.parts_target})</span>
                    </div>
                    <div style={{ height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${overview.shift_pace.parts_pct}%`, height: '100%', background: overview.shift_pace.status === 'behind' ? '#dc2626' : overview.shift_pace.status === 'ahead' ? '#16a34a' : '#f59e0b' }} />
                    </div>
                  </div>
                  <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#374151' }}>
                    {overview.shift_pace.on_pace_delta > 5 ? (
                      <>✅ <strong>Ahead by {overview.shift_pace.on_pace_delta}%</strong> — keep the pace.</>
                    ) : overview.shift_pace.on_pace_delta < -5 ? (
                      <>⚠️ <strong>Behind by {Math.abs(overview.shift_pace.on_pace_delta)}%</strong> — need to push or extend.</>
                    ) : (
                      <>👍 <strong>On pace</strong> — within ±5% of expected.</>
                    )}
                  </div>
                  <DrilldownChat context="Are we on pace to hit shift target? What needs to change?" />
                </div>
              )}

              {/* OPERATOR PRODUCTIVITY */}
              {overview && !overview.error && overviewTab === 'operators' && overview.operator_productivity && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>👨‍🔧 Operator Productivity Today</div>
                  {overview.operator_productivity.note && (
                    <div style={{ background: '#fef3c7', color: '#92400e', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
                      {overview.operator_productivity.note}
                    </div>
                  )}
                  {(overview.operator_productivity.rows || []).map((op, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: i === 0 ? '#fde047' : i === 1 ? '#e5e7eb' : '#fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{op.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{op.machines_count} machines · {op.machines.join(', ')}</div>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{op.parts}</div>
                    </div>
                  ))}
                  <DrilldownChat context="Which operator is producing the most today? Why?" />
                </div>
              )}
                  </div>
                </>,
                document.body
              )}
            </div>
          )}

          {/* ASK VIEW (existing chat) */}
          {view === 'ask' && (<>
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "12px",
              backgroundColor: "#fafafa",
            }}
          >
            {history.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mode === "floor"
                  ? "Ask anything about the whole floor, or pick a quick prompt below."
                  : "Ask anything about this shift, or pick a quick prompt below."}
              </Typography>
            )}
            {history.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: "85%",
                    padding: "8px 12px",
                    borderRadius: 12,
                    backgroundColor:
                      msg.role === "user"
                        ? "#F3951D"
                        : msg.role === "error"
                        ? "#ffebee"
                        : "#ffffff",
                    color:
                      msg.role === "user"
                        ? "white"
                        : msg.role === "error"
                        ? "#c62828"
                        : "#212121",
                    border: msg.role === "assistant" ? "1px solid #e0e0e0" : "none",
                    whiteSpace: "pre-wrap",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Smart Buddy is thinking...
                </Typography>
              </Stack>
            )}
          </div>

          {history.length === 0 && (
            <div style={{ padding: "8px 12px", borderTop: "1px solid #eee", backgroundColor: "#fff" }}>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                {quickActions.map((qa) => (
                  <Chip
                    key={qa.label}
                    label={qa.label}
                    size="small"
                    onClick={() => ask(qa.q)}
                    disabled={!ready || loading}
                    sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fff3e0" } }}
                  />
                ))}
              </Stack>
            </div>
          )}

          <Stack
            direction="row"
            spacing={1}
            sx={{ p: 1, borderTop: "1px solid #eee", bgcolor: "#fff" }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={
                ready
                  ? mode === "floor"
                    ? "Ask about the whole floor..."
                    : "Ask about this shift..."
                  : "Select a shift first"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  ask(input);
                }
              }}
              disabled={!ready || loading}
            />
            <Button
              variant="contained"
              onClick={() => ask(input)}
              disabled={!ready || loading || !input.trim()}
              sx={{
                minWidth: 0,
                bgcolor: "#F3951D",
                "&:hover": { bgcolor: "#d97706" },
              }}
            >
              <SendIcon />
            </Button>
          </Stack>
          </>)}
        </Paper>
      )}
    </>
  );
}
